import {
  type LucideIcon,
  Users,
  UserCheck,
  CreditCard,
  Receipt,
  BookOpen,
  Sparkles,
  ClipboardCheck,
  Megaphone,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/content/EmptyState";
import { AnimatedNumber } from "@/components/dashboard/AnimatedNumber";
import { useAdminDashboard } from "@/hooks/useAdmin";
import { formatRupees } from "@/lib/format";
import { signupSourceLabel } from "@/lib/signupSource";

type Stat = {
  label: string;
  value: number;
  icon: LucideIcon;
  /** Optional per-frame formatter for the animated value. */
  format?: (n: number) => string;
  hint?: string;
};

function StatCard({ stat }: { stat: Stat }) {
  const Icon = stat.icon;
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-muted">{stat.label}</p>
          <p className="mt-2 font-display text-3xl font-bold tracking-tight tabular-nums">
            <AnimatedNumber value={stat.value} format={stat.format} />
          </p>
          {stat.hint ? <p className="mt-1 text-xs text-text-subtle">{stat.hint}</p> : null}
        </div>
        <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export function AdminDashboardPage() {
  const { data, isLoading, isError } = useAdminDashboard();

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        icon={Users}
        title="Couldn't load dashboard"
        body="The platform stats failed to load. Try refreshing the page."
      />
    );
  }

  const stats: Stat[] = [
    { label: "Total users", value: data.users.total, icon: Users },
    { label: "Active users", value: data.users.active, icon: UserCheck },
    { label: "Active subscriptions", value: data.subscriptions.active, icon: CreditCard },
    {
      label: "Payments this month",
      value: data.payments_mtd.count,
      icon: Receipt,
      hint: `${formatRupees(data.payments_mtd.total_inr)} collected`,
    },
    { label: "Curriculum chapters", value: data.curriculum.chapters, icon: BookOpen },
  ];

  if (data.onboarding) {
    const { students, completed } = data.onboarding;
    stats.splice(2, 0, {
      label: "Students onboarded",
      value: completed,
      icon: ClipboardCheck,
      hint: `of ${students} students · ${students - completed} incomplete`,
    });
  }

  const sources = Object.entries(data.signup_sources ?? {}).sort((a, b) => b[1] - a[1]);
  const sourcesTotal = sources.reduce((n, [, c]) => n + c, 0);

  if (data.ai_spend_mtd) {
    stats.push({
      label: "AI tests this month",
      value: data.ai_spend_mtd.tests_generated,
      icon: Sparkles,
      hint: `${data.ai_spend_mtd.test_tokens.toLocaleString("en-IN")} tokens`,
    });
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {stats.map((stat) => (
        <StatCard key={stat.label} stat={stat} />
      ))}

      {/* Revenue MTD given its own emphasised tile (money is the headline). */}
      <Card className="p-5 sm:col-span-2 lg:col-span-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-text-muted">Revenue this month</p>
            <p className="mt-2 font-display text-4xl font-bold tracking-tight tabular-nums text-brand">
              <AnimatedNumber
                value={data.payments_mtd.total_inr}
                format={(n) => formatRupees(n)}
              />
            </p>
          </div>
          <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl bg-brand-soft text-brand">
            <Receipt className="h-6 w-6" />
          </div>
        </div>
      </Card>

      {/* Where students say they heard about us (onboarding answer). */}
      {sourcesTotal > 0 ? (
        <Card className="p-5 sm:col-span-2 lg:col-span-3">
          <div className="mb-4 flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-brand" />
            <p className="text-sm font-medium text-text-muted">How students heard about StaxED</p>
          </div>
          <ul className="flex flex-col gap-2.5">
            {sources.map(([source, count]) => (
              <li key={source} className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-3 text-sm">
                <span className="truncate text-text">{signupSourceLabel(source)}</span>
                <span className="h-2 overflow-hidden rounded-full bg-surface-2">
                  <span
                    className={source === "unknown" ? "block h-full bg-border-strong" : "block h-full bg-brand"}
                    style={{ width: `${(count / sourcesTotal) * 100}%` }}
                  />
                </span>
                <span className="tabular-nums text-text-muted">{count}</span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}
